"use client";
import SendIcon from "@mui/icons-material/Send";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import LazyPhoneInput, {
  validatePhone,
} from "~/components/inputs/LazyPhoneInput";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { customerContactService } from "~/connectors/contact.connector";
import { customerUsersService } from "~/connectors/users.connector";
import type { User } from "~/types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";

export default function ContactForm() {
  const { data: session } = useSession();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch user profile if logged in
  useEffect(() => {
    const fetchProfile = async () => {
      if (session?.user) {
        setIsLoadingProfile(true);
        try {
          const profile = await customerUsersService.getProfile();
          setUserProfile(profile);

          // Prefill form with user data
          setFormData((prev) => ({
            ...prev,
            email: profile.email || "",
            name: profile.name || "",
            phone: profile.phone || "",
          }));
        } catch (error) {
          console.error("Error fetching user profile:", error);
        } finally {
          setIsLoadingProfile(false);
        }
      }
    };

    fetchProfile();
  }, [session]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await customerContactService.submitContactForm(formData);

      // Track contact form submission
      trackEvent(AnalyticsEvents.CONTACT_FORM_SUBMITTED);

      setSubmitted(true);

      // If user provided name/phone that weren't in their profile, refetch profile
      if (session?.user && (shouldShowName || shouldShowPhone)) {
        try {
          const updatedProfile = await customerUsersService.getProfile();
          setUserProfile(updatedProfile);
        } catch (error) {
          console.error("Error refetching user profile:", error);
        }
      }

      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      alert(
        "A apărut o eroare la trimiterea mesajului. Te rugăm să încerci din nou.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine which fields to show/hide
  const shouldShowEmail = !session?.user; // Hide email if logged in
  const shouldShowName = !userProfile?.name; // Hide name if it exists in profile
  const shouldShowPhone = !userProfile?.phone; // Hide phone if it exists in profile

  // Form is valid if name, email and message are filled, and phone is valid (if provided)
  const isFormValid =
    formData.name.trim() &&
    formData.email.trim() &&
    formData.message.trim() &&
    formData.email.includes("@") &&
    validatePhone(formData.phone);

  if (submitted) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: 3, fontFamily: "var(--font-body)" }}>
          Mulțumim pentru mesaj!
        </Typography>
        <Typography sx={{ mb: 4 }}>
          Am primit mesajul tău și îți vom răspunde în cel mai scurt timp
          posibil.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSubmitted(false);
            // Repopulate form with user data if logged in
            if (userProfile) {
              setFormData((prev) => ({
                ...prev,
                email: userProfile.email || "",
                name: userProfile.name || "",
                phone: userProfile.phone || "",
              }));
            }
          }}
          size="large"
        >
          Trimite Alt Mesaj
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, fontFamily: "var(--font-body)" }}>
        Trimite-ne un mesaj
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        {shouldShowName && (
          <TextField
            fullWidth
            label="Nume"
            variant="outlined"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            required
          />
        )}

        {(shouldShowEmail || shouldShowPhone) && (
          <Box sx={{ display: "flex", gap: 2 }}>
            {shouldShowEmail && (
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
              />
            )}
            {shouldShowPhone && (
              <LazyPhoneInput
                label="Telefon (opțional)"
                value={formData.phone}
                onChange={(value) => handleInputChange("phone", value)}
              />
            )}
          </Box>
        )}

        <TextField
          fullWidth
          label="Mesaj"
          multiline
          rows={6}
          variant="outlined"
          value={formData.message}
          onChange={(e) => handleInputChange("message", e.target.value)}
          required
          placeholder="Scrie mesajul tău aici..."
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!isFormValid || isSubmitting || isLoadingProfile}
          endIcon={<SendIcon />}
          sx={{
            mt: 2,
            py: 2,
            fontSize: "1.1rem",
          }}
        >
          {isSubmitting ? "Se trimite..." : "Trimite Mesajul"}
        </Button>
      </Box>
    </Paper>
  );
}
